import { AccessTime } from '@mui/icons-material';
import { Autocomplete, InputAdornment, SxProps, TextField, Theme } from '@mui/material';
import { JSX, Ref, SyntheticEvent, useEffect, useState } from 'react';
import {
    clockToMinutes,
    DEFAULT_TIME_STEP,
    matchesTyped,
    nearestOption,
    parseTypedTime,
    timeOptions,
} from '../../library/timeOptions';
import { toTimeInputValue } from '../../library/dateTimeHelper';
import { pickerFieldStyles, pickerLabelClearanceStyles, timeListStyles } from './appPickers.styles';

export interface AppTimePickerProps {
    label: string;
    /** `HH:mm`. The API's `HH:mm:ss` is accepted and trimmed. */
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    name?: string;
    inputRef?: Ref<HTMLInputElement>;
    disabled?: boolean;
    required?: boolean;
    fullWidth?: boolean;
    error?: boolean;
    helperText?: string;
    /** Minutes between offered times. 15 by default. */
    minutesStep?: number;
    /** Narrows the list. Bounds the *offer*, never what may be typed. */
    minTime?: string;
    maxTime?: string;
    /**
     * The exact times to offer, for a caller that knows which of them are real. Given, no grid
     * is generated and `minutesStep`, `minTime` and `maxTime` are all ignored.
     */
    options?: string[];
    /**
     * Selection only: the value must be one of `options`, and a time nobody offered cannot be
     * typed in either.
     *
     * Off by default, and it must stay that way. Every other caller here records a time that
     * *happened* -- a session given at 09:07 is a real row -- and only the diary knows which
     * times are bookable, because only the diary has asked the API.
     */
    strict?: boolean;
    /** What the popup says when `options` is empty. Reachable in strict mode alone. */
    noOptionsText?: string;
    sx?: SxProps<Theme>;
}

/**
 * A time of day, as one list you can type at.
 *
 * This replaced two things, and both were worse. The native `<input type="time">` was a
 * different widget in every browser and a full-height spinner wheel on iOS. What briefly
 * replaced *that* was MUI's two-column digital clock -- an hour column and a minute column
 * -- which asks for two decisions and a scroll to make one choice, and which opened a popup
 * wider than the dialog it lived in.
 *
 * The shape here is the one the US Web Design System settles on for scheduling, and which
 * Smashing Magazine's survey of date and time pickers reaches independently: **a combobox
 * over a single list of real times, filtered by what you type.** Nobody thinks "nine, then
 * thirty"; they think "half nine", and they can now type `930` and be done.
 *
 * Four properties are deliberate:
 *
 *   * **Typing beats the list, and does not fight it.** `9` means 09:00, `930` and `0930`
 *     both mean 09:30, and any punctuation between the digits is ignored. The list filters
 *     on digits with the colon removed from both sides, so typing `930` still *shows* 09:30
 *     rather than emptying the list on the way to it.
 *   * **A time not on the step is kept, not rounded.** A session recorded at 09:07 is a
 *     real row; `freeSolo` keeps it and the list merely opens next to it.
 *   * **24-hour, always.** AM/PM is the single commonest source of a booking made twelve
 *     hours out, and the clinic's whole diary is 24-hour already.
 *   * **Nothing is bounded by default.** `minTime`/`maxTime` narrow what is *offered* where
 *     a caller genuinely knows better, and never what may be typed -- a therapist called in
 *     at eight in the evening still has to be able to write it down.
 *
 * `strict` is the one exception to that last property, and it is opt-in for a reason. A caller
 * that has asked the API which windows are free knows something none of the others do, so
 * offering a list *and* accepting anything typed past it would be offering an answer while
 * inviting a different one. In that mode the list is the whole of what may be chosen: typing
 * still narrows it, and still lands on `09:30` from `930`, but only onto a time that is on it.
 * Everything above holds unchanged wherever `strict` is absent, which is everywhere else.
 */
export const AppTimePicker = (props: AppTimePickerProps): JSX.Element => {
    const step: number = props.minutesStep ?? DEFAULT_TIME_STEP;
    const options: string[] = props.options ?? timeOptions(step, props.minTime, props.maxTime);
    const strict: boolean = !!props.strict;

    /** What is stored, normalised -- the API's `HH:mm:ss` renders as `HH:mm`. */
    const committed: string = toTimeInputValue(props.value);

    /*
     * The text in the box is its own state while it is being typed at: `9` is not yet a
     * time, and pushing every keystroke through `onChange` would rewrite the field under
     * the cursor. It is reconciled below whenever the committed value changes -- which is
     * what makes a `reset()` from react-hook-form reach the input.
     */
    const [typed, setTyped] = useState<string>(committed);

    /*
     * Whether the list is being narrowed *by the keyboard*.
     *
     * Without this the field filters against its own value the moment it is opened, so
     * clicking a box reading 09:00 offers exactly one option -- itself -- and the list
     * becomes useless for changing anything. Opening shows the whole day, scrolled to
     * where you already are; typing is what narrows it.
     */
    const [filtering, setFiltering] = useState<boolean>(false);

    useEffect(() => setTyped(committed), [committed]);

    /** Reads whatever is in the box and keeps it only if it is a time. */
    const commit = (raw: string): void => {
        if (raw.trim() === '') {
            props.onChange('');
            return;
        }

        const parsed: string | null = parseTypedTime(raw);

        /*
         * In strict mode a reading that is not on offer is not a value. `autoSelect` below has
         * already committed anything the list could match, so what reaches here is either a
         * time nobody offered or an unreadable entry -- and both mean the box goes back to
         * whatever was chosen, rather than storing a time the caller said is unavailable.
         */
        if (strict && (parsed === null || !options.includes(parsed))) {
            setTyped(committed);
            return;
        }

        // An unreadable entry falls back to what was already stored rather than clearing
        // it: a half-typed "9:" is on its way somewhere, not a request to empty the field.
        setTyped(parsed ?? committed);

        if (parsed) {
            props.onChange(parsed);
        }
    };

    return (
        <Autocomplete
            freeSolo={!strict}
            /*
             * So that typing `930` and tabbing away still lands on 09:30 when 09:30 is on
             * offer. Without `freeSolo` there is no blur-commit of a typed value at all, and
             * the keyboard would be reduced to scrolling the list.
             */
            autoSelect={strict}
            disableClearable={false}
            disabled={props.disabled}
            options={options}
            noOptionsText={props.noOptionsText}
            /*
             * Pinned to the nearest option so the list opens *at* the time being held even
             * when that time is not on the step. `null` rather than the raw value, or MUI
             * warns about a value it cannot find among the options.
             *
             * Strict mode cannot use `nearestOption`: a value off the list is one the caller
             * says is unavailable, and pinning it to its neighbour would show a time nobody
             * chose sitting selected. It reports empty instead, which is what it is.
             */
            value={(strict
                ? (options.includes(committed) ? committed : undefined)
                : nearestOption(committed, options)) ?? null}
            inputValue={typed}
            fullWidth={props.fullWidth}
            sx={props.sx ?? pickerFieldStyles}
            openOnFocus
            autoHighlight
            selectOnFocus
            handleHomeEndKeys
            onOpen={() => setFiltering(false)}
            filterOptions={(all: string[]): string[] =>
                (filtering ? all.filter((option: string) => matchesTyped(option, typed)) : all)}
            onInputChange={(_event: SyntheticEvent, next: string, reason: string) => {
                // A pick from the list is committed by onChange below; this only tracks
                // the keyboard, so that reselecting the same option cannot blank the box.
                if (reason !== 'input') {
                    return;
                }

                setTyped(next);
                setFiltering(true);

                /*
                 * Committed the moment the text *is* a time, and not before. Typing `9`
                 * also parses -- to 09:00 -- but somebody on their way to 09:30 has not
                 * finished, so a half-entry waits for blur. `07:00` in full has, so it
                 * lands immediately and the field behaves like the plain input it replaced.
                 */
                if (clockToMinutes(next) !== null) {
                    props.onChange(next);
                } else if (next.trim() === '') {
                    props.onChange('');
                }
            }}
            onChange={(_event: SyntheticEvent, next: string | null) => {
                if (next !== null) {
                    setTyped(next);
                    setFiltering(false);
                    props.onChange(next);
                }
            }}
            onBlur={() => {
                setFiltering(false);
                commit(typed);
                props.onBlur?.();
            }}
            slotProps={{ listbox: { sx: timeListStyles } }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={props.name}
                    inputRef={props.inputRef}
                    label={props.label}
                    required={props.required}
                    error={props.error}
                    helperText={props.helperText}
                    size="small"
                    placeholder="09:00"
                    slotProps={{
                        inputLabel: { sx: pickerLabelClearanceStyles },
                        input: {
                            ...params.InputProps,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <AccessTime fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            )}
        />
    );
};
